import { useMemo, useState } from "react"
import { TitleBar } from "@/components/ui/TitleBar"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { ExternalLink, Link as LinkIcon, Pin, PinOff, Star, Trash2, X } from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import { ApiError } from "@/core/api"
import {
  createLink,
  deleteLink,
  linksKey,
  linksQueryOptions,
  updateLink,
  type EventLink,
  type LinkInput,
} from "@/core/links"
import {
  Field,
  GhostButton,
  PrimaryButton,
  TextInput,
  Toggle,
} from "@/components/settings/primitives"
import { useElementWidth } from "@/components/ui/resizable"

// Below this container width the edit form can't sit beside the list as a
// second column (list ~380 + form ~360), so it falls back to a centered dialog.
const SIDE_BY_SIDE_MIN = 720

export const Route = createFileRoute("/$orgSlug/$eventSlug/links")({
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(linksQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: LinksView,
})

function LinksView() {
  const { eventId } = useActiveEvent()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<EventLink | "new" | null>(null)

  const linksQ = useQuery({ ...linksQueryOptions(eventId ?? ""), enabled: !!eventId })
  const links = useMemo(() => linksQ.data ?? [], [linksQ.data])

  const invalidate = () => void qc.invalidateQueries({ queryKey: linksKey(eventId ?? "") })
  const onError = (e: unknown) =>
    toast.error(e instanceof ApiError ? e.message : "Something went wrong")

  const pinMut = useMutation({
    mutationFn: (v: { linkId: string; quickLink: number }) =>
      updateLink(v.linkId, { quickLink: v.quickLink }),
    onSuccess: (_r, v) => {
      toast.success(v.quickLink ? "Pinned to Home" : "Unpinned")
      invalidate()
    },
    onError,
  })

  const delMut = useMutation({
    mutationFn: (linkId: string) => deleteLink(linkId),
    onSuccess: () => {
      toast.success("Link deleted")
      invalidate()
    },
    onError,
  })

  const config = useMemo<CollectionConfig<EventLink>>(
    () => ({
      title: "Links",
      getId: (l) => l.id,
      searchText: (l) => `${l.title} ${l.url}`,
      onNew: () => setEditing("new"),
      onOpen: (l) => setEditing(l),
      newLabel: "Add link",
      defaultSortId: "order",
      defaultSortDir: "asc",
      emptyTitle: "No links yet",
      emptyBlurb:
        "Menus, maps, ticketing, dress code — curated URLs your guests open from the app's Home.",
      emptyIcon: <LinkIcon className="h-5 w-5" />,
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "pinned", label: "Pinned", filter: (l) => !!l.quickLink },
      ],
      bulkActions: [
        {
          id: "pin",
          label: "Pin to Home",
          icon: <Pin className="h-4 w-4" />,
          run: (rows) =>
            rows
              .filter((l) => !l.quickLink)
              .forEach((l) => pinMut.mutate({ linkId: l.id, quickLink: 1 })),
        },
        {
          id: "unpin",
          label: "Unpin",
          icon: <PinOff className="h-4 w-4" />,
          run: (rows) =>
            rows
              .filter((l) => !!l.quickLink)
              .forEach((l) => pinMut.mutate({ linkId: l.id, quickLink: 0 })),
        },
        {
          id: "delete",
          label: "Delete",
          icon: <Trash2 className="h-4 w-4" />,
          run: (rows) => {
            if (confirm(`Delete ${rows.length} link${rows.length === 1 ? "" : "s"}?`))
              rows.forEach((l) => delMut.mutate(l.id))
          },
        },
      ],
      columns: [
        {
          id: "title",
          header: "Title",
          sortable: true,
          sortValue: (l) => l.title.toLowerCase(),
          render: (l) => (
            <div className="flex items-center gap-2.5">
              <LinkIcon className="h-4 w-4 shrink-0 text-plum-700/55 dark:text-plum-200/40" />
              <span className="truncate font-semibold text-plum-900 dark:text-plum-50">
                {l.title || "Untitled"}
              </span>
              {!!l.quickLink && (
                <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />
              )}
            </div>
          ),
        },
        {
          id: "url",
          header: "URL",
          className: "text-plum-700/70 dark:text-plum-200/55",
          sortable: true,
          sortValue: (l) => l.url.toLowerCase(),
          render: (l) => (
            <a
              href={l.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex max-w-full items-center gap-1 truncate hover:text-[var(--accent)] hover:underline"
            >
              <span className="truncate">{prettyUrl(l.url)}</span>
              <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
            </a>
          ),
        },
        {
          id: "pinned",
          header: "Pinned",
          render: (l) => {
            const pinned = !!l.quickLink
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (pinMut.isPending) return
                  pinMut.mutate({ linkId: l.id, quickLink: pinned ? 0 : 1 })
                }}
                title={pinned ? "Unpin from Home" : "Pin to Home"}
                className="grid h-7 w-7 place-items-center rounded-md text-plum-700/40 transition hover:bg-plum-100/60 dark:text-plum-200/30 dark:hover:bg-white/5"
              >
                <Star
                  className={`h-4 w-4 ${
                    pinned ? "fill-amber-400 text-amber-400" : "text-plum-700/35 dark:text-plum-200/30"
                  }`}
                />
              </button>
            )
          },
        },
      ],
    }),
    [pinMut, delMut],
  )

  const [width, widthRef] = useElementWidth()
  const sideBySide = !!editing && !!eventId && width >= SIDE_BY_SIDE_MIN

  const formProps =
    editing && eventId
      ? {
          eventId,
          link: editing === "new" ? null : editing,
          onClose: () => setEditing(null),
          onSaved: () => {
            setEditing(null)
            invalidate()
          },
        }
      : null

  return (
    <div ref={widthRef} className="relative flex h-full">
      <div className="min-w-0 flex-1">
        <CollectionView
          config={config}
          rows={links}
          loading={linksQ.isLoading && links.length === 0}
          error={linksQ.isError ? ((linksQ.error as Error)?.message ?? "Failed to load links") : null}
        />
      </div>
      {/* Wide: the editor takes column 2 beside the list. Narrow: a dialog. */}
      {formProps && sideBySide && (
        <div className="w-[360px] shrink-0 border-l border-plum-200/40 dark:border-white/8">
          <LinkForm
            key={editing === "new" ? "new" : (editing as EventLink).id}
            variant="panel"
            {...formProps}
          />
        </div>
      )}
      {formProps && !sideBySide && <LinkForm variant="dialog" {...formProps} />}
    </div>
  )
}

const URL_RE = /^https?:\/\/.+/i

function LinkForm({
  eventId,
  link,
  onClose,
  onSaved,
  variant,
}: {
  eventId: string
  link: EventLink | null
  onClose: () => void
  onSaved: () => void
  /** "panel" = column 2 beside the list; "dialog" = centered modal overlay. */
  variant: "panel" | "dialog"
}) {
  const [title, setTitle] = useState(link?.title ?? "")
  const [url, setUrl] = useState(link?.url ?? "")
  const [icon, setIcon] = useState(link?.icon ?? "")
  const [pinned, setPinned] = useState(!!link?.quickLink)

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      const input: LinkInput = {
        title: title.trim(),
        url: url.trim(),
        icon: icon.trim() || null,
        quickLink: pinned ? 1 : 0,
      }
      if (link) await updateLink(link.id, input)
      else await createLink(eventId, input)
    },
    onSuccess: () => {
      toast.success(link ? "Link saved" : "Link added")
      onSaved()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Save failed"),
  })

  const valid = title.trim().length > 0 && URL_RE.test(url.trim())
  const submit = () => {
    if (valid && !save.isPending) save.mutate()
  }

  const header = (
    <div className="flex items-center gap-2 border-b border-plum-200/50 px-5 py-3.5 dark:border-white/10">
      <span className="text-[var(--accent)] dark:text-plum-200/70">
        <LinkIcon className="h-[18px] w-[18px]" />
      </span>
      <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
        {link ? "Edit link" : "New link"}
      </h2>
      <button
        onClick={onClose}
        className="ml-auto grid h-7 w-7 place-items-center rounded text-plum-700/55 transition hover:bg-plum-100/60 dark:text-plum-200/45 dark:hover:bg-white/5"
        title="Close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )

  const body = (
    <form
      className="flex-1 space-y-4 overflow-y-auto scroll-thin px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <Field label="Title">
        <TextInput
          autoFocus
          value={title}
          placeholder="Restaurant menu"
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field label="URL" hint="Must start with http:// or https://">
        <TextInput
          type="url"
          value={url}
          placeholder="https://…"
          onChange={(e) => setUrl(e.target.value)}
          className={url.trim() && !URL_RE.test(url.trim()) ? "border-rose-400" : ""}
        />
      </Field>
      <Field label="Icon" hint="Optional symbol name shown in the app (e.g. map, menu, ticket).">
        <TextInput value={icon} placeholder="map" onChange={(e) => setIcon(e.target.value)} />
      </Field>
      <Toggle
        label="Pin to Home"
        description="Surface this link as a quick link on the attendee Home."
        checked={pinned}
        onChange={setPinned}
      />
      <button type="submit" className="hidden" />
    </form>
  )

  const footer = (
    <div className="flex items-center justify-end gap-2 border-t border-plum-200/50 px-5 py-3.5 dark:border-white/10">
      <GhostButton onClick={onClose}>Cancel</GhostButton>
      <PrimaryButton onClick={submit} disabled={!valid || save.isPending}>
        {save.isPending ? "Saving…" : link ? "Save" : "Add link"}
      </PrimaryButton>
    </div>
  )

  // Column 2: a plain full-height panel beside the list (no scrim). Uses the
  // shared TitleBar (the standard record header) instead of the dialog chrome.
  if (variant === "panel") {
    return (
      <div className="flex h-full flex-col bg-surface">
        <TitleBar title={link ? "Edit link" : "New link"} onClose={onClose} />
        {body}
        {footer}
      </div>
    )
  }

  // Narrow: a centered modal overlay over the list.
  return (
    <div className="absolute inset-0 z-40 flex items-start justify-center p-4 pt-[8vh]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-plum-950/40 backdrop-blur-sm dark:bg-black/50"
      />
      <div className="relative z-10 flex max-h-full w-[min(94%,30rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-fluent-lg ring-1 ring-plum-200/50 dark:bg-surface-raised dark:ring-white/10">
        {header}
        {body}
        {footer}
      </div>
    </div>
  )
}

function prettyUrl(url: string): string {
  return url.replace(/^https?:\/\//i, "").replace(/\/$/, "")
}
